import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { CardContent } from "@/components/ui/card";
import { ReportConfigV2, ReportExtras } from "@/common/report-configuration";
import BarChartComponentV2 from "@/components/charts/barChartV2";
import PieChartComponentV2 from "@/components/charts/pieChartV2";
// import AreaChartComponent from "@/components/charts/areaChart";
import LineChartComponentV2 from "@/components/charts/lineChartV2";
// import RadarChartComponent from "@/components/charts/radarChart";
// import RadialChartComponent from "@/components/charts/radialChart";
import HorizontalBarChartComponentV2 from "@/components/charts/horizontalBarChartV2";
import { exportDivAsPngWithInputFix } from "@/lib/chartExport.ts";
import { limitReportData } from "@/common/dataLimiter";
import { transformToChartData } from "@/common/chartDataTransformer";
import { useCurrentWorkspace } from "@/store/authContext/authContext";
import { HandleLegendItemChangeArguments } from "@/types/report";
import { findTimeDimensionV2 } from "@/common/dimensions";
import { getAxisLabels } from "@/common/common-utils";
import { MEASURES } from "@/common/measures";
import { ReportStateReducerActionType } from "@/common/reportDesignerStateReducer";
import FunnelChartComponentV2 from "@/components/charts/funnelChartV2";
import { Teaser } from "@/components/charts/components/teaser";
import { ReferenceLineInfo } from "@/types/chart";

export interface DataItem {
  [key: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  value: number;
  value_num_employees?: number;
}

interface ReportChartViewProps {
  data: DataItem[];
  reportConfig: ReportConfigV2;
  reportExtras: ReportExtras;
  reportDesignerDispatch?: React.Dispatch<ReportStateReducerActionType>;
  showToggle?: boolean;
  size: string;
  onExportPng?: (exportFn: () => Promise<void>) => void;
}

const ReportChartViewV2: React.FC<ReportChartViewProps> = ({
  data,
  reportConfig,
  reportExtras,
  reportDesignerDispatch: dispatch,
  size,
  onExportPng,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  console.log(`RC`, reportConfig);
  // Generate a unique ID for this chart instance
  const chartId = useMemo(
    () => `chart-${reportConfig.id || Math.random().toString(36).substr(2, 9)}`,
    [reportConfig.id],
  );

  const handleExportPng = React.useCallback(async () => {
    await exportDivAsPngWithInputFix(containerRef, reportConfig.title);
  }, [containerRef, reportConfig.title]);

  useEffect(() => {
    if (onExportPng) {
      onExportPng(handleExportPng);
    }
  }, [onExportPng, handleExportPng]);
  const workspace = useCurrentWorkspace();
  // Apply limit to data before processing
  const limitedData = useMemo(() => {
    const timeDimension = findTimeDimensionV2(reportConfig.data);
    return limitReportData(
      data,
      {
        limit: reportConfig.chart.limit,
        chartType: reportConfig.chart.chartType,
        dimensions: reportConfig.data.dimensions,
        measures: reportConfig.data.measures,
      },
      workspace.recruitmentStageColumnNames,
      timeDimension ?? undefined,
    );
  }, [data, reportConfig, workspace.recruitmentStageColumnNames]);

  // Chart data processing
  const { chartData, seriesConfig } = useMemo(() => {
    return transformToChartData(limitedData, reportConfig);
  }, [limitedData, reportConfig]);

  console.log(`chartData:`, chartData);
  console.log(`seriesConfig:`, seriesConfig);

  const receiveChartChangesAndPotentiallyUpdateConfig = useCallback(
    ({ key, field, newValue }: HandleLegendItemChangeArguments) => {
      if (!dispatch) return;

      dispatch({
        type: "storeLegendItemChanges",
        payload: {
          key,
          field,
          newValue,
          seriesConfig,
        },
      });
    },
    [dispatch, seriesConfig],
  );

  const renderChart = () => {
    const getMaxHeight = (size: string) => {
      switch (size) {
        case "lg":
          return 600;
        case "md":
          return 400;
        default:
          return 300;
      }
    };

    const fieldNameOverrides =
      workspace.dataset_field_configs[reportConfig.data.datasetName]
        ?.fieldNameOverrides;
    const { yAxisLeftLabel, yAxisRightLabel, xAxisLabel } = getAxisLabels(
      reportConfig,
      fieldNameOverrides,
    );

    const commonProps = {
      data: chartData,
      reportExtras,
      xAxis: "name",
      seriesConfig,
      maxHeight: getMaxHeight(size),
      handleLegendItemChange: receiveChartChangesAndPotentiallyUpdateConfig,
      chartSettings: reportConfig.chart,
      xAxisLabel,
      yAxisLeftLabel,
      yAxisRightLabel,
      referenceLines: [] as ReferenceLineInfo[],
    };

    if (reportConfig.data.measures.includes("COMPENSATION_GAP")) {
      commonProps.referenceLines.push({
        y: 0,
        yAxisId: "compensation_gap",
        label: "Equal Pay Line",
        stroke: "#000",
      });
    }

    if (
      reportConfig.data.measures.includes("ATTRITION_RATE_BY_SEGMENT") &&
      typeof reportExtras.attritionRate === "number"
    ) {
      commonProps.referenceLines.push({
        y: reportExtras.attritionRate,
        yAxisId: "generic_percentage",
        label: "Company Average",
        stroke: "#000",
      });
    }

    switch (reportConfig.chart.chartType) {
      case "Pie":
        return <PieChartComponentV2 {...commonProps} />;
      // case CHART_TYPES_V2.AREA:
      //   return <AreaChartComponent {...commonProps} />;
      // case CHART_TYPES_V2.RADAR:
      //   return <RadarChartComponent {...commonProps} />;
      // case CHART_TYPES_V2.RADIAL:
      //   return <RadialChartComponent {...commonProps} />;
      case "Line":
        return <LineChartComponentV2 {...commonProps} />;
      case "Horizontal Bar":
        return <HorizontalBarChartComponentV2 {...commonProps} />;
      case "Horizontal Stacked Bar":
        return (
          <HorizontalBarChartComponentV2 {...commonProps} stacked={true} />
        );
      case "Stacked Bar":
        return <BarChartComponentV2 {...commonProps} stacked={true} />;
      case "Funnel":
        return <FunnelChartComponentV2 {...commonProps} />;
      default:
        return <BarChartComponentV2 {...commonProps} />;
    }
  };

  const teaser = reportConfig.data.measures.some(
    (m) => MEASURES[m].columnsReferenced[0] === "NOT_IMPLEMENTED",
  );
  return (
    <>
      {!teaser && (
        <div className="h-full flex flex-col">
          <div
            className={`flex-1 min-h-0 ${
              size === "lg"
                ? "h-[700px]"
                : size === "md"
                  ? "h-[400px]"
                  : "h-[300px]"
            }`}
          >
            <CardContent className="p-0">
              <div id={chartId} className="h-full" ref={containerRef}>
                {chartData.length > 0 && seriesConfig.size > 0 ? (
                  renderChart()
                ) : (
                  <div className="block text-center my-[150px] text-muted-foreground">
                    <p>
                      Looks like we don't have enough data for this chart yet.
                    </p>
                    <p>Add more data to unlock it.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </div>
        </div>
      )}
      {teaser && (
        <div className="h-full flex flex-col">
          <div
            className={`flex-1 min-h-0 ${
              size === "lg"
                ? "h-[700px]"
                : size === "md"
                  ? "h-[400px]"
                  : "h-[300px]"
            }`}
          >
            <CardContent className="p-0">
              <div className="h-full flex items-center justify-center text-muted-foreground">
                <Teaser fill="#CCCCCC" />
              </div>
            </CardContent>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportChartViewV2;
